import { TInputSelect } from '../forms/InputSelect';
import { RefElement, TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description ProductVariantSelect implementation for variation based on Select HTML element
 * @param InputSelect Base widget for extending
 * @returns Product VariantSelect widget
 */
function ProductVariantSelectBoxClassCreator(InputSelect: TInputSelect) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductVariantSelectBox
     * @augments TInputSelect
     *
     * @example <caption>Product variations VariantSelect based on Select widget usage</caption>
     * <div
     *     class="b-variations_item m-option m-${attr.id}"
     *     data-widget="inputSelect"
     *     data-widget-event-change="changeAttribute"
     *     data-id="attr-${attr.id}"
     *     data-skip-validation="true"
     * >
     *     <div data-ref="container">
     *         <label
     *             class="b-variations_item-label ${attr.id}"
     *             for="${attr.id}-${attrLoopState}"
     *         >
     *             ${attr.displayName}:
     *         </label>
     *         <div class="b-variations_item-content">
     *             <div class="b-select">
     *                 <select
     *                     class="b-select-input m-${attr.id}"
     *                     id="${attr.id}-${attrLoopState}"
     *                     aria-describedby="variation-error-${attrLoopState}"
     *                     data-ref="field"
     *                     data-event-change="onChange"
     *                 >
     *                     <option data-attr-url="${attr.resetUrl}">
     *                         ${Resource.msg('label.select', 'common', null)} ${attr.displayName}
     *                     </option>
     *                     <isloop items="${attr.values}" var="attrValue">
     *                         <option
     *                             value="${attrValue.value}"
     *                             data-attr-url="${attrValue.url}"
     *                             ${!attrValue.selectable ? 'disabled' : ''}
     *                             ${attrValue.selected ? 'selected' : ''}
     *                         >
     *                             ${attrValue.displayValue}
     *                         </option>
     *                     </isloop>
     *                 </select>
     *                 <isinclude template="common/icons/standalone/selectIcon"/>
     *             </div>
     *         </div>
     *         <div
     *             role="alert"
     *             id="variation-error-${attrLoopState}"
     *             class="b-variations_item-error"
     *             data-ref="errorFeedback"
     *             hidden="hidden"
     *         ></div>
     *     </div>
     *     <script type="template/mustache" data-ref="template">
     *         <div data-ref="container">
     *             ...
     *         </div>
     *     </script>
     * </div>
     */
    class ProductVariantSelectBox extends InputSelect {
        selectedVariation: null | TRefElementInstance = null;

        /**
         * @description saves selected variation to local field
         * @param variation - selected variation
         * @emits 'change'
         */
        selectVariation(variation: TRefElementInstance) {
            const field = variation.get<HTMLSelectElement>();

            if (field && field.selectedOptions) {
                this.selectedVariation = new RefElement(Array.from(field.selectedOptions));
            }

            this.emit('change', this);
        }

        /**
         * @description gets selected variation element
         * @returns selected variation or ref element of empty array
         */
        getSelectedVariations(): TRefElementInstance {
            if (this.selectedVariation) {
                return this.selectedVariation;
            }

            return new RefElement([]);
        }
    }

    return ProductVariantSelectBox;
}

export type TProductVariantSelectBox = ReturnType<typeof ProductVariantSelectBoxClassCreator>;

export type TProductVariantSelectBoxInstance = InstanceType<TProductVariantSelectBox>;

export default ProductVariantSelectBoxClassCreator;
